import { useState } from 'react';
import { Link } from 'react-router';
import { normalizePhone, normalizeSearch, uuidv7, validateEmail } from '@pos/core';
import { applyLocalChanges, type LocalChange, type SupplierRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, Field, Input, PageHeader, Spinner, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { buildSupplierDeleteChanges } from './changes.ts';
import { useCatalog } from './data.ts';

interface SupplierForm {
  id: string | null;
  name: string;
  phone: string;
  email: string;
  cuit: string;
  notes: string;
}

const EMPTY: SupplierForm = { id: null, name: '', phone: '', email: '', cuit: '', notes: '' };
const orNull = (s: string) => (s.trim() === '' ? null : s.trim());

export function SuppliersPage() {
  const { business, writer } = useSession();
  const catalog = useCatalog();
  const [form, setForm] = useState<SupplierForm | null>(null);
  const [toDelete, setToDelete] = useState<SupplierRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!catalog) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  const counts = new Map<string, number>();
  for (const i of catalog.items) if (i.supplier) counts.set(i.supplier.id, (counts.get(i.supplier.id) ?? 0) + 1);

  const run = async (changes: LocalChange[]) => {
    setBusy(true);
    setError(null);
    try {
      await applyLocalChanges(db, changes, writer);
      requestSync();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form) return;
    const name = form.name.trim();
    const cuit = form.cuit.replace(/\D/g, '');
    if (name.length < 2) return setError('El nombre debe tener al menos 2 caracteres.');
    if (catalog.suppliers.some((s) => s.id !== form.id && normalizeSearch(s.name) === normalizeSearch(name))) {
      return setError('Ya existe un proveedor con ese nombre.');
    }
    if (form.email.trim() && validateEmail(form.email)) return setError(validateEmail(form.email));
    if (cuit && cuit.length !== 11) return setError('El CUIT tiene que tener 11 números.');
    const fields = {
      name,
      phone: orNull(normalizePhone(form.phone)),
      email: orNull(form.email),
      cuit: cuit || null,
      notes: orNull(form.notes),
    };
    const now = new Date().toISOString();
    const change: LocalChange = form.id
      ? { table: 'suppliers', kind: 'patch', id: form.id, changes: fields }
      : { table: 'suppliers', kind: 'insert', row: { id: uuidv7(), business_id: business.id, ...fields, deleted_at: null, created_at: now, updated_at: now } };
    if (await run([change])) setForm(null);
  };

  const edit = (s: SupplierRow) =>
    setForm({ id: s.id, name: s.name, phone: s.phone ?? '', email: s.email ?? '', cuit: s.cuit ?? '', notes: s.notes ?? '' });

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader
        title="Proveedores"
        description="A quién le comprás. Sirve para filtrar productos y, más adelante, para cargar compras."
        actions={
          <>
            <Link to="/productos">
              <Button variant="secondary">Volver</Button>
            </Link>
            <Button onClick={() => setForm(EMPTY)}>Nuevo proveedor</Button>
          </>
        }
      />
      {error && !form && <Alert tone="error">{error}</Alert>}

      <Card flush className="divide-y divide-slate-100">
        {catalog.suppliers.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Todavía no hay proveedores.</p>}
        {catalog.suppliers.map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-3 sm:px-4">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{s.name}</p>
              <p className="truncate text-sm text-slate-500">
                {[s.phone, s.email, `${counts.get(s.id) ?? 0} productos`].filter(Boolean).join(' · ')}
              </p>
            </div>
            <Button variant="ghost" onClick={() => edit(s)}>
              Editar
            </Button>
            <Button variant="dangerGhost" onClick={() => setToDelete(s)}>
              Eliminar
            </Button>
          </div>
        ))}
      </Card>

      <Dialog
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Editar proveedor' : 'Nuevo proveedor'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setForm(null)}>
              Cancelar
            </Button>
            <Button onClick={save} loading={busy}>
              Guardar
            </Button>
          </>
        }
      >
        {form && (
          <div className="space-y-3">
            {error && <Alert tone="error">{error}</Alert>}
            <Field label="Nombre">{(id) => <Input id={id} value={form.name} autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} />}</Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Teléfono">{(id) => <Input id={id} type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />}</Field>
              <Field label="CUIT (opcional)">{(id) => <Input id={id} inputMode="numeric" value={form.cuit} onChange={(e) => setForm({ ...form, cuit: e.target.value })} />}</Field>
            </div>
            <Field label="Email">{(id) => <Input id={id} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />}</Field>
            <Field label="Notas">{(id) => <Input id={id} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />}</Field>
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        title={`¿Eliminar "${toDelete?.name}"?`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setToDelete(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={async () => {
                if (toDelete && (await run(buildSupplierDeleteChanges(toDelete.id, catalog.items.map((i) => i.product), new Date().toISOString())))) {
                  setToDelete(null);
                }
              }}
            >
              Eliminar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">Sus productos quedan sin proveedor (no se borran).</p>
      </Dialog>
    </div>
  );
}
